import type { CSSProperties, ReactNode } from 'react';

interface SectionHeadingProps {
  /** Uppercase mono kicker — 章節分類詞（"Chapter II" / "Shadow work" 等）。 */
  kicker?: ReactNode;
  /** Display serif title — 主標題。 */
  title: ReactNode;
  /** 可選副標 / lede。 */
  subtitle?: ReactNode;
  /** 對齊（default: 'left'）。 */
  align?: 'left' | 'center';
  /** Heading 視覺等級（影響字級，不影響 DOM tag — DOM 一律 h2）。 */
  level?: 'lg' | 'xl';
  className?: string;
  id?: string;
}

/**
 * Editorial 章節標題 — Report / Pricing 用。
 *
 * 視覺結構：
 *   [kicker — uppercase mono small, accent color]
 *   [title  — display serif, large, heading color]
 *   [subtitle — body, muted, max 56ch]
 *
 * Tokens 全部走 themes.css var()，三個 theme 自動跟換 typography。
 */
export default function SectionHeading({
  kicker,
  title,
  subtitle,
  align = 'left',
  level = 'lg',
  className,
  id,
}: SectionHeadingProps) {
  const titleStyle: CSSProperties = {
    fontFamily: 'var(--font-display)',
    color: 'var(--color-heading)',
    letterSpacing: 'var(--h1-tracking, normal)',
    fontWeight: 'var(--h1-weight, 500)' as unknown as number,
    fontSize: level === 'xl' ? 'clamp(2rem, 4.5vw, 3.25rem)' : 'clamp(1.5rem, 3vw, 2.25rem)',
    lineHeight: 1.1,
    margin: 0,
  };

  return (
    <header
      id={id}
      className={['flex flex-col gap-3', align === 'center' ? 'items-center text-center' : '', className]
        .filter(Boolean)
        .join(' ')}
      style={align === 'center' ? { textAlign: 'center' } : undefined}
    >
      {kicker && (
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: '11px',
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: 'var(--color-accent)',
          }}
        >
          {kicker}
        </span>
      )}
      <h2 style={titleStyle}>{title}</h2>
      {subtitle && (
        <p
          style={{
            fontFamily: 'var(--font-body)',
            color: 'var(--color-muted)',
            fontSize: '1.0625rem',
            lineHeight: 1.55,
            margin: 0,
            maxWidth: align === 'center' ? '56ch' : undefined,
          }}
        >
          {subtitle}
        </p>
      )}
    </header>
  );
}
